import { NATIONAL_SECTION_LEGACY_ID } from "../constants";
import type { CrmSectionOption } from "../types";

export type GroupEmailScope = {
  key: string;
  label: string;
  description: string;
  national: boolean;
  sectionId?: string;
};

export function assignedSectionLegacyIdsForGroupEmail(sectionLegacyIds: readonly string[]): string[] {
  return sectionLegacyIds.filter((legacyId) => legacyId.length > 0 && legacyId !== NATIONAL_SECTION_LEGACY_ID);
}

export function canAccessGroupEmail({
  canViewPersonContact,
  canAccessNationalGroupEmail,
  assignedSectionLegacyIds,
  loaded,
  sectionLegacyIdsLoading
}: {
  canViewPersonContact: boolean;
  canAccessNationalGroupEmail: boolean;
  assignedSectionLegacyIds: readonly string[];
  loaded: boolean;
  sectionLegacyIdsLoading: boolean;
}): boolean {
  if (!loaded || sectionLegacyIdsLoading || !canViewPersonContact) return false;
  return canAccessNationalGroupEmail || assignedSectionLegacyIdsForGroupEmail(assignedSectionLegacyIds).length > 0;
}

export function filterAssignedCrmSections(
  sections: readonly CrmSectionOption[],
  assignedSectionLegacyIds: readonly string[]
): CrmSectionOption[] {
  const allowed = new Set(assignedSectionLegacyIdsForGroupEmail(assignedSectionLegacyIds));
  return sections.filter(
    (section) =>
      section.legacyId !== null && section.legacyId !== NATIONAL_SECTION_LEGACY_ID && allowed.has(section.legacyId)
  );
}

export function resolveGroupEmailScopes({
  canAccessNationalGroupEmail,
  assignedSections
}: {
  canAccessNationalGroupEmail: boolean;
  assignedSections: readonly CrmSectionOption[];
}): GroupEmailScope[] {
  const scopes: GroupEmailScope[] = [];

  if (canAccessNationalGroupEmail) {
    scopes.push({
      key: "national",
      label: "National Section",
      description: "PGA HQ committees",
      national: true
    });
  }

  for (const section of assignedSections) {
    scopes.push({
      key: section.id,
      label: section.name,
      description: "Your assigned section",
      national: false,
      sectionId: section.id
    });
  }

  return scopes;
}

export function defaultGroupEmailScopeKey(
  scopes: readonly GroupEmailScope[],
  preferredSectionId?: string
): string | undefined {
  if (preferredSectionId) {
    const preferred = scopes.find((scope) => scope.sectionId === preferredSectionId);
    if (preferred) return preferred.key;
  }
  return scopes[0]?.key;
}
